'use client';

import { useEffect, useRef, useState } from 'react';

export default function MobileSectionIndicator({ sections = [] }) {
  const [active, setActive] = useState(sections[0]?.id || '');

  useEffect(() => {
    if (!sections || sections.length === 0) return undefined;

    const targets = sections.map((s) => document.getElementById(s.id)).filter(Boolean);

    if (targets.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0,
      }
    );

    targets.forEach((t) => observer.observe(t));

    return () => observer.disconnect();
  }, [sections]);

  // when active changes, auto-scroll the indicator container to center the active button
  const containerRef = useRef(null);
  useEffect(() => {
    if (!active || !containerRef.current) return;

    const timer = setTimeout(() => {
      const btn = document.getElementById(`indicator-btn-${active}`);
      if (!btn) return;
      // Calculate scroll position to center the button
      const btnLeft = btn.offsetLeft;
      const btnWidth = btn.offsetWidth;
      const containerWidth = containerRef.current.clientWidth;
      const scrollOffset = btnLeft + btnWidth / 2 - containerWidth / 2;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      containerRef.current.scrollTo({
        left: scrollOffset,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [active]);

  const handleClick = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Calculate scroll position with offset for scroll (40px)
    const offset = 40;
    const targetY = el.offsetTop - offset;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: targetY, behavior: reduceMotion ? 'auto' : 'smooth' });
    setActive(id);
  };

  if (!sections || sections.length === 0) return null;

  return (
    <nav className="md:hidden sticky top-0 z-40 bg-gradient-to-t from-white/80 to-white backdrop-blur-[6px] supports-[backdrop-filter]:backdrop-blur-[6px] max-w-[100vw] [@media(pointer:fine)]:max-w-[calc(100vw-8px)] overflow-hidden">
      <div ref={containerRef} className="overflow-x-auto overscroll-x-contain scrollbar-none">
        <div className="inline-flex gap-3 px-4 py-2.5">
          {sections.map((s) => {
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                id={`indicator-btn-${s.id}`}
                type="button"
                onClick={() => handleClick(s.id)}
                className={`whitespace-nowrap text-[#323232] leading-[1] rounded-full px-3 py-2.25 text-sm font-semibold transition-colors hover:bg-[#EFEFEF] hover:text-[#151515] ${
                  isActive ? 'bg-[#C9C9C9]' : 'bg-white'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
